import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { endpoints, paymentRequired, type FamilyMember, type PantryEntry, type RecipeFull, type Swap } from '../api/endpoints';
import { ApiException } from '../api/client';
import { Skeleton, ErrorState } from '../components/states';
import { UpgradeModal } from '../components/UpgradeModal';
import { CookedModal } from '../components/CookedModal';
import { AddToMenuModal } from '../components/AddToMenuModal';
import { track } from '../lib/analytics';
import { scaleAmount, clampServings } from '../lib/servings';
import { buildRecipeShoppingText } from '../lib/recipe-shopping-text';
import { shareShoppingText, copyText } from '../lib/share';

function chipClass(active: boolean): string {
  return active
    ? 'rounded-full bg-primary-600 px-3 py-1 text-sm font-medium text-white'
    : 'rounded-full bg-white px-3 py-1 text-sm text-ink shadow-sm';
}

export function RecipeDetailScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState<RecipeFull | null>(null);
  const [servingsN, setServingsN] = useState<number | null>(null);
  const [buyToast, setBuyToast] = useState<string | null>(null);
  const [pantryIds, setPantryIds] = useState<Set<string>>(new Set());
  const [family, setFamily] = useState<FamilyMember[]>([]);
  const [activeMemberId, setActiveMemberId] = useState<string | null>(null);
  const [swapsByMember, setSwapsByMember] = useState<Record<string, Swap[]>>({});
  const [adaptLoading, setAdaptLoading] = useState(false);
  const [adaptError, setAdaptError] = useState<string | null>(null);
  const [upsell, setUpsell] = useState<{ requiredTier: 'pro' | 'family'; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [cookedOpen, setCookedOpen] = useState(false);
  const [cookedError, setCookedError] = useState<string | null>(null);
  const [fav, setFav] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const [r, pantry, fam] = await Promise.all([
        endpoints.getRecipe(id),
        endpoints.getPantry(),
        endpoints.getFamily().catch(() => [] as FamilyMember[]),
      ]);
      setRecipe(r);
      setFav(r.isFavorite ?? false);
      track('recipe_open');
      setFamily(fam);
      setPantryIds(
        new Set(
          (pantry as PantryEntry[])
            .map((e) => e.product?.id)
            .filter((x): x is string => Boolean(x)),
        ),
      );
    } catch (e) {
      if (e instanceof ApiException && e.status === 404) setNotFound(true);
      else setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить рецепт');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    // Намеренный fetch-in-effect (план без TanStack): разовый setState на маунте — приемлемо.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const selectMember = useCallback(
    async (memberId: string) => {
      setActiveMemberId(memberId);
      setAdaptError(null);
      if (swapsByMember[memberId]) return; // кеш сессии: повторный тап без сети
      setAdaptLoading(true);
      try {
        const { swaps } = await endpoints.adaptRecipe(id, memberId);
        setSwapsByMember((prev) => ({ ...prev, [memberId]: swaps }));
      } catch (e) {
        const pr = paymentRequired(e);
        if (pr) {
          setActiveMemberId(null); // адаптация не выполнена (гейт) → назад на «Оригинал», иначе ложное «Замены не требуются»
          setUpsell(pr);
          return;
        }
        setAdaptError(e instanceof ApiException ? e.body.message : 'Не удалось подобрать замены');
      } finally {
        setAdaptLoading(false);
      }
    },
    [id, swapsByMember],
  );

  const activeSwaps = useMemo<Swap[]>(
    () => (activeMemberId ? (swapsByMember[activeMemberId] ?? []) : []),
    [activeMemberId, swapsByMember],
  );
  const swapMap = useMemo(() => {
    const m = new Map<string, Swap>();
    for (const s of activeSwaps) m.set(s.original.trim().toLowerCase(), s);
    return m;
  }, [activeSwaps]);

  if (loading) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="p-6 text-center">
        <p className="text-lg font-semibold text-ink">Рецепт не найден</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-primary-600">
          Назад
        </button>
      </div>
    );
  }
  if (error || !recipe) {
    return (
      <div className="p-4">
        <ErrorState message={error ?? 'Ошибка'} onRetry={() => void load()} />
      </div>
    );
  }

  const effN = servingsN ?? recipe.servings ?? 1;

  const hasShoppingContent =
    (recipe.ingredients ?? []).length > 0 || (recipe.extraIngredients ?? []).length > 0;
  const buyText = () =>
    buildRecipeShoppingText({
      title: recipe.title ?? 'Рецепт',
      servings: recipe.servings,
      n: effN,
      ingredients: recipe.ingredients ?? [],
      extras: recipe.extraIngredients ?? [],
      pantryIds,
    });
  const onBuy = async () => {
    const r = await shareShoppingText(buyText());
    if (r === 'copied') setBuyToast('Список скопирован');
    else if (r === 'failed') setBuyToast('Не получилось — попробуйте «Скопировать»');
  };
  const onCopyBuy = async () =>
    setBuyToast((await copyText(buyText())) ? 'Список скопирован' : 'Не удалось скопировать');

  const kbju = recipe.kbjuPerServing;
  const meta = [
    recipe.cookTimeMin ? `${recipe.cookTimeMin} мин` : null,
    recipe.servings ? `${recipe.servings} порц.` : null,
    kbju?.kcal ? `${kbju.kcal} ккал` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  function renderName(name: string | undefined, isOptional: boolean | undefined) {
    const safeName = name ?? '';
    const swap = swapMap.get(safeName.trim().toLowerCase());
    if (swap) {
      return (
        <>
          <span className="text-ink/40 line-through">{safeName}</span>
          {' → '}
          <span className="font-semibold text-primary-700">{swap.replacement}</span>
          {swap.reason && <span className="block text-xs font-normal text-ink/50">{swap.reason}</span>}
        </>
      );
    }
    return (
      <>
        {safeName}
        {isOptional ? ' (по желанию)' : ''}
      </>
    );
  }

  // IIFE, а не useMemo: вычисление стоит после ранних return (loading/notFound/error),
  // где хук нарушил бы правила хуков; список крошечный, мемоизация не нужна.
  const inPantryIngredients = (() => {
    const seen = new Set<string>();
    const out: { productId: string; name: string }[] = [];
    for (const ing of recipe.ingredients ?? []) {
      const pid = ing.productId;
      if (!pid || !pantryIds.has(pid) || seen.has(pid)) continue;
      seen.add(pid);
      out.push({ productId: pid, name: ing.productName ?? '' });
    }
    return out;
  })();

  async function toggleFav() {
    const next = !fav;
    setFav(next); // оптимистично
    track(next ? 'favorite_add' : 'favorite_remove');
    try {
      if (next) await endpoints.addFavorite(id);
      else await endpoints.removeFavorite(id);
    } catch {
      setFav(!next); // откат
      setToast(next ? 'Не удалось добавить в избранное' : 'Не удалось убрать');
    }
  }

  async function handleCooked(productIds: string[]) {
    setCookedOpen(false);
    if (productIds.length === 0) return;
    const results = await Promise.allSettled(productIds.map((pid) => endpoints.removePantryProduct(pid)));
    if (results.every((r) => r.status === 'fulfilled')) {
      setPantryIds((prev) => {
        const next = new Set(prev);
        for (const pid of productIds) next.delete(pid);
        return next;
      });
      return;
    }
    // Часть не удалилась — перечитать холодильник, чтобы UI отражал правду.
    try {
      const pantry = await endpoints.getPantry();
      setPantryIds(
        new Set(
          (pantry as PantryEntry[]).map((e) => e.product?.id).filter((x): x is string => Boolean(x)),
        ),
      );
    } catch {
      /* оставляем текущее состояние */
    }
    setCookedError('Не удалось убрать часть продуктов — список обновлён');
  }

  return (
    <div className="min-h-screen bg-cream pb-10">
      <div className="relative h-52 w-full bg-gradient-to-br from-sand to-primary-300">
        {recipe.imageUrl && (
          <img src={recipe.imageUrl} alt={recipe.title} className="h-52 w-full object-cover" />
        )}
        <button
          onClick={() => navigate(-1)}
          aria-label="Назад"
          className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1.5 text-ink shadow"
        >
          ←
        </button>
        <button
          onClick={() => void toggleFav()}
          aria-pressed={fav}
          aria-label={fav ? 'Убрать из избранного' : 'В избранное'}
          className={`absolute right-3 top-3 rounded-full bg-white/90 p-2 shadow ${fav ? 'text-primary-600' : 'text-ink/50'}`}
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill={fav ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M12 21s-7-4.6-9.3-9C1.4 9.3 2.6 6 6 6c2 0 3.2 1.2 4 2.4C10.8 7.2 12 6 14 6c3.4 0 4.6 3.3 3.3 6-2.3 4.4-9.3 9-9.3 9z" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <div className="px-4 pt-4">
        <h1 className="text-2xl font-bold text-ink">{recipe.title}</h1>
        {meta && <p className="mt-1 text-sm text-ink/60">{meta}</p>}

        {recipe.servings ? (
          <div className="mt-3 flex items-center gap-3">
            <span className="text-sm text-ink/70">На сколько человек:</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Меньше порций"
                onClick={() => setServingsN(clampServings(effN - 1))}
                className="h-8 w-8 rounded-full bg-white text-lg leading-none text-primary-700 shadow-sm"
              >
                −
              </button>
              <span aria-label="Число человек" className="w-6 text-center font-semibold text-ink">
                {effN}
              </span>
              <button
                type="button"
                aria-label="Больше порций"
                onClick={() => setServingsN(clampServings(effN + 1))}
                className="h-8 w-8 rounded-full bg-white text-lg leading-none text-primary-700 shadow-sm"
              >
                +
              </button>
            </div>
          </div>
        ) : null}

        {family.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-ink/70">Адаптировать для:</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setActiveMemberId(null);
                  setAdaptError(null);
                }}
                className={chipClass(activeMemberId === null)}
              >
                Оригинал
              </button>
              {family.map((m) => {
                const mid = m.id;
                if (!mid) return null;
                return (
                  <button
                    key={mid}
                    type="button"
                    onClick={() => void selectMember(mid)}
                    className={chipClass(activeMemberId === mid)}
                  >
                    {m.name}
                  </button>
                );
              })}
            </div>
            {adaptLoading && <p className="mt-2 text-sm text-ink/50">Подбираю замены…</p>}
            {adaptError && <p className="mt-2 text-sm text-red-600">{adaptError}</p>}
            {activeMemberId && !adaptLoading && !adaptError && activeSwaps.length === 0 && (
              <p className="mt-2 text-sm text-ink/60">Замены не требуются — рецепт подходит.</p>
            )}
            {activeMemberId && !adaptLoading && activeSwaps.length > 0 && (
              <p className="mt-1 text-xs text-ink/50">Показаны замены. КБЖУ — для оригинала.</p>
            )}
          </div>
        )}

        <h2 className="mb-2 mt-6 font-semibold text-ink">Ингредиенты</h2>
        <ul className="space-y-1">
          {(recipe.ingredients ?? []).map((ing, i) => {
            const have = ing.productId ? pantryIds.has(ing.productId) : false;
            return (
              <li key={ing.productId ?? i} className="flex items-start justify-between">
                <span className={have ? 'text-ink' : 'font-medium text-primary-600'}>
                  {renderName(ing.productName, ing.isOptional)}
                </span>
                <span className="text-sm text-ink/50">
                  {scaleAmount(ing.amount, recipe.servings, effN, ing.unit) ?? ''} {ing.unit ?? ''}
                  {!have ? ' · нет дома' : ''}
                </span>
              </li>
            );
          })}
        </ul>

        {(recipe.extraIngredients ?? []).length > 0 && (
          <ul className="mt-1 space-y-1">
            {(recipe.extraIngredients ?? []).map((ing, i) => (
              <li key={`extra-${i}`} className="flex items-start justify-between">
                <span className="text-ink">{renderName(ing.name, ing.isOptional)}</span>
                <span className="text-sm text-ink/50">
                  {scaleAmount(ing.amount, recipe.servings, effN, ing.unit) ?? ''} {ing.unit ?? ''}
                </span>
              </li>
            ))}
          </ul>
        )}

        <h2 className="mb-2 mt-6 font-semibold text-ink">Приготовление</h2>
        <ol className="list-decimal space-y-2 pl-5">
          {(recipe.steps ?? []).map((s, i) => (
            <li key={i} className="text-ink/90">
              {s}
            </li>
          ))}
        </ol>

        {kbju && (
          <div className="mt-6 grid grid-cols-4 gap-2 text-center">
            {(
              [
                ['Ккал', kbju.kcal],
                ['Белки', kbju.protein],
                ['Жиры', kbju.fat],
                ['Углеводы', kbju.carbs],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="rounded-xl bg-white p-2 shadow-sm">
                <p className="text-sm font-semibold text-ink">{value ?? '—'}</p>
                <p className="text-xs text-ink/50">{label}</p>
              </div>
            ))}
          </div>
        )}

        {hasShoppingContent && (
          <div className="mt-6">
            <div className="flex gap-2">
              <button
                onClick={() => void onBuy()}
                className="flex-1 rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white active:scale-[0.98]"
              >
                Купить продукты
              </button>
              <button
                onClick={() => void onCopyBuy()}
                className="rounded-xl bg-white px-4 py-3 text-sm font-semibold text-primary-700 shadow-sm"
              >
                Скопировать
              </button>
            </div>
            {buyToast && (
              <p role="status" className="mt-2 text-center text-sm text-ink/60">
                {buyToast}
              </p>
            )}
          </div>
        )}

        <button
          onClick={() => {
            setCookedError(null);
            setCookedOpen(true);
          }}
          className="mt-3 w-full rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white shadow-card active:scale-[0.98]"
        >
          Приготовила
        </button>
        {cookedError && <p className="mt-2 text-sm text-red-600">{cookedError}</p>}
        <button
          onClick={() => setMenuOpen(true)}
          className="mt-3 w-full rounded-xl bg-white px-4 py-3 font-semibold text-primary-700 shadow-card active:scale-[0.98]"
        >
          В меню
        </button>
      </div>

      {upsell && <UpgradeModal requiredTier={upsell.requiredTier} message={upsell.message} onClose={() => setUpsell(null)} />}
      {cookedOpen && (
        <CookedModal
          items={inPantryIngredients}
          onConfirm={(ids) => void handleCooked(ids)}
          onClose={() => setCookedOpen(false)}
        />
      )}
      {menuOpen && (
        <AddToMenuModal
          recipeId={id}
          recipeTitle={recipe.title}
          onAdded={() => setToast('Добавлено в меню')}
          onClose={() => setMenuOpen(false)}
        />
      )}
      {toast && (
        <p role="status" className="fixed inset-x-0 bottom-6 mx-auto w-fit rounded-full bg-ink/80 px-4 py-2 text-sm text-white shadow">
          {toast}
        </p>
      )}
    </div>
  );
}
